import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, X, CheckCircle, AlertCircle, Send } from 'lucide-react';
import axios from 'axios';
import logger from '../../utils/logger';
import { API_URL } from '../../utils/apiConfig';

const InviteModal = ({ isOpen, onClose, onSuccess, defaultLanguage = 'fr' }) => {
  const { isDark } = useTheme();
  const { t } = useLanguage();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [invitLanguage, setInvitLanguage] = useState(defaultLanguage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setRole('member');
      setInvitLanguage(defaultLanguage);
      setError('');
      setSuccess('');
      setLoading(false);
    }
  }, [isOpen, defaultLanguage]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/companies/me/invite`, {
        email,
        role,
        language: invitLanguage,
      });
      setSuccess(t('team.inviteSuccess').replace('{email}', email));
      onSuccess?.();
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      logger.error('Failed to send invitation:', err);
      setError(err.response?.data?.detail || t('team.errorGeneric'));
      setLoading(false);
    }
  };

  const inputCls = `w-full px-4 py-2.5 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
    isDark
      ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
  }`;
  const labelCls = `block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('team.inviteModalTitle')}
          </h3>
          <button
            onClick={onClose}
            disabled={loading || !!success}
            className={`p-2 rounded-lg disabled:opacity-50 ${isDark ? 'text-slate-400 hover:bg-slate-700/50' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className={labelCls}>{t('team.inviteEmailLabel')}</label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="invite-email-input"
                  className={`${inputCls} pl-10`}
                  placeholder="utilisateur@example.com"
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label className={labelCls}>{t('team.inviteRoleLabel')}</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="invite-role-select"
                className={inputCls}
              >
                <option value="member">{t('team.roleMember')}</option>
                <option value="admin">{t('team.roleAdmin')}</option>
              </select>
            </div>

            {/* Language */}
            <div>
              <label className={labelCls}>{t('team.inviteLanguageLabel')}</label>
              <select
                value={invitLanguage}
                onChange={(e) => setInvitLanguage(e.target.value)}
                data-testid="invite-language-select"
                className={inputCls}
              >
                <option value="fr">{t('common.languages.fr')}</option>
                <option value="de">{t('common.languages.de')}</option>
              </select>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 ${
                  isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="invite-submit-btn"
                className="flex-1 px-4 py-2.5 rounded-xl font-medium bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('team.inviteSending')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('team.inviteSubmit')}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InviteModal;
